/*
  Hola Books — HB carousel (assets/hb-carousel.js)

  Drives the paddles of every [data-hb-carousel] root: one card per click,
  disabled at the ends, the row hidden when the track fits. It also brings
  a keyboard-focused card to its snap position when only part of it is in
  view (the browser does that by itself only for a card that is fully out
  of view). The scroller itself is CSS scroll-snap and works without this
  file.

  A root is wired once and marked data-hb-carousel-ready. Roots that arrive
  later (a section the theme fetches, a section the editor re-renders) are
  picked up by a MutationObserver on <body> and by shopify:section:load.
*/
(function () {
  'use strict';

  if (window.hbCarousel) {
    return;
  }

  var ROOT = '[data-hb-carousel]';
  var instances = new WeakMap();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function scrollBehavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  function focusIsVisible(element) {
    try {
      return element.matches(':focus-visible');
    } catch (error) {
      return true;
    }
  }

  function init(root) {
    if (!root || root.nodeType !== 1) {
      return;
    }
    if (root.dataset.hbCarouselReady === 'true') {
      return;
    }

    var scroller = root.querySelector('[data-hb-carousel-scroller]');
    var track = scroller ? scroller.querySelector('.hb-carousel__track') : null;
    if (!scroller || !track) {
      return;
    }
    root.dataset.hbCarouselReady = 'true';

    var paddles = root.querySelector('[data-hb-carousel-paddles]');
    var prev = root.querySelector('[data-hb-carousel-prev]');
    var next = root.querySelector('[data-hb-carousel-next]');
    var clicked = null;
    var clickedTimer = null;
    var pending = -1;
    var pendingTimer = null;
    var CLICKED_SETTLE_MS = 300;
    var PENDING_SETTLE_MS = 160;

    if (root.closest('.carousel')) {
      root.addEventListener('focusin', function (event) {
        event.stopPropagation();
      });
    }

    track.addEventListener('focusin', onTrackFocus);

    if (!paddles || !prev || !next) {
      return;
    }

    function readState() {
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;
      var left = scroller.scrollLeft;

      if (track.children.length < 2) {
        return 'fits';
      }
      if (maxScroll <= 1) {
        return 'fits';
      }
      if (left <= 0) {
        return 'at-start';
      }
      if (Math.ceil(left + scroller.clientWidth) >= scroller.scrollWidth) {
        return 'at-end';
      }
      return 'between';
    }

    /* The paddle that was clicked stays remembered only while its own
       scroll is still moving: every scroll event pushes the deadline out,
       and 300ms after the last one it is forgotten. */
    function arm(button) {
      clicked = button || null;
      if (clicked) {
        settleClicked();
      }
    }

    function disarm() {
      clearTimeout(clickedTimer);
      clickedTimer = null;
      clicked = null;
    }

    function settleClicked() {
      clearTimeout(clickedTimer);
      clickedTimer = setTimeout(disarm, CLICKED_SETTLE_MS);
    }

    /* The card a click is heading for, so a second click during the smooth
       scroll steps from there and not from the in-flight position. It is
       forgotten 160ms after the last scroll event, and at once when the
       visitor takes over (pointer, touch, wheel, keyboard focus). */
    function armPending() {
      if (pending < 0) {
        return;
      }
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(clearPending, PENDING_SETTLE_MS);
    }

    function clearPending() {
      clearTimeout(pendingTimer);
      pendingTimer = null;
      pending = -1;
    }

    function update() {
      var state = readState();
      var focused = document.activeElement;

      if (clicked) {
        settleClicked();
      }

      switch (state) {
        case 'fits':
          paddles.hidden = true;
          prev.disabled = true;
          next.disabled = true;
          break;
        case 'at-start':
          paddles.hidden = false;
          prev.disabled = true;
          next.disabled = false;
          break;
        case 'at-end':
          paddles.hidden = false;
          prev.disabled = false;
          next.disabled = true;
          break;
        case 'between':
          paddles.hidden = false;
          prev.disabled = false;
          next.disabled = false;
          break;
      }

      handOffFocus(focused);
    }

    function onScroll() {
      update();
      armPending();
    }

    /* Focus moves to the other paddle only when the clicked paddle has
       just been disabled AND still held focus at that moment (read before
       the disabled flag flipped, so it does not depend on whether the
       browser blurs a disabled button). Focus that has moved on, to a card
       or anywhere else, is never taken back, and the page never scrolls. */
    function handOffFocus(focused) {
      var other;
      var hadFocus;

      if (!clicked) {
        return;
      }
      if (!clicked.disabled) {
        return;
      }
      other = clicked === next ? prev : next;
      hadFocus = focused === clicked;
      disarm();
      if (!hadFocus) {
        return;
      }
      if (other.disabled) {
        return;
      }
      other.focus({ preventScroll: true });
    }

    function positions() {
      var items = track.children;
      var list = [];
      var i;

      if (!items.length) {
        return list;
      }
      for (i = 0; i < items.length; i += 1) {
        list.push(items[i].offsetLeft - items[0].offsetLeft);
      }
      return list;
    }

    function currentIndex(list, left) {
      var nearest = 0;
      var i;

      for (i = 1; i < list.length; i += 1) {
        if (Math.abs(list[i] - left) < Math.abs(list[nearest] - left)) {
          nearest = i;
        }
      }
      if (left > list[nearest] + 1 && nearest < list.length - 1) {
        nearest += 1;
      }
      return nearest;
    }

    function step(direction, button) {
      var list = positions();
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;
      var current;
      var target;

      if (!list.length) {
        return;
      }
      if (pending >= 0) {
        current = pending;
      } else {
        current = currentIndex(list, scroller.scrollLeft);
      }
      target = current + direction;
      if (target < 0) {
        target = 0;
      }
      if (target > list.length - 1) {
        target = list.length - 1;
      }

      /* The browser clamps the last positions to the end of the scroll, so
         the pending index is read at the clamped landing: otherwise a prev
         click from the end would be swallowed. */
      pending = currentIndex(list, Math.min(list[target], maxScroll));
      armPending();
      arm(button);
      scroller.scrollTo({
        left: list[target],
        behavior: scrollBehavior()
      });
    }

    function onTrackFocus(event) {
      var target = event.target;
      var item = target && target.closest ? target.closest('.hb-carousel__item') : null;
      var padStart;
      var padEnd;
      var itemRect;
      var scrollerRect;
      var list;
      var index;

      if (!item || item.parentElement !== track) {
        return;
      }
      clearPending();
      if (readState() === 'fits') {
        return;
      }
      if (!focusIsVisible(target)) {
        return;
      }

      padStart = parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;
      padEnd = parseFloat(getComputedStyle(scroller).scrollPaddingRight) || 0;
      itemRect = item.getBoundingClientRect();
      scrollerRect = scroller.getBoundingClientRect();
      if (itemRect.left >= scrollerRect.left + padStart - 1 && itemRect.right <= scrollerRect.right - padEnd + 1) {
        return;
      }

      list = positions();
      index = Array.prototype.indexOf.call(track.children, item);
      if (index < 0 || index >= list.length) {
        return;
      }
      scroller.scrollTo({
        left: list[index],
        behavior: scrollBehavior()
      });
    }

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('pointerdown', clearPending, { passive: true });
    scroller.addEventListener('touchstart', clearPending, { passive: true });
    scroller.addEventListener('wheel', clearPending, { passive: true });

    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(update);
      observer.observe(scroller);
      observer.observe(track);
    } else {
      window.addEventListener('resize', update);
    }

    instances.set(root, { step: step, update: update });
    update();
  }

  function initAll(scope) {
    if (!scope || scope.nodeType !== 1) {
      return;
    }
    if (scope.matches(ROOT)) {
      init(scope);
    }
    scope.querySelectorAll(ROOT).forEach(init);
  }

  function onClick(event) {
    var button = event.target.closest('[data-hb-carousel-prev], [data-hb-carousel-next]');
    var root;
    var instance;

    if (!button) {
      return;
    }
    root = button.closest(ROOT);
    instance = root ? instances.get(root) : null;
    if (!instance) {
      return;
    }
    if (button.hasAttribute('data-hb-carousel-next')) {
      instance.step(1, button);
    } else {
      instance.step(-1, button);
    }
  }

  function onSectionLoad(event) {
    initAll(event.target);
  }

  function onBlockSelect(event) {
    var item = event.target && event.target.closest ? event.target.closest('.hb-carousel__item') : null;

    if (!item) {
      return;
    }
    item.scrollIntoView({ block: 'nearest', inline: 'start' });
  }

  function onMutation(records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(initAll);
    });
  }

  function start() {
    initAll(document.documentElement);
    new MutationObserver(onMutation).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', onClick);
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:block:select', onBlockSelect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.hbCarousel = { init: init };
}());
